import { html, render } from 'lit-html';
import type { Ref } from 'lit-html/directives/ref.js';
import { createRef, ref } from 'lit-html/directives/ref.js';

import addViewIcon from '@disy/cadenza-icons/add-view.svg';
import copyWorksheetIcon from '@disy/cadenza-icons/copy-worksheet.svg';
import settingsIcon from '@disy/cadenza-icons/settings.svg';
import trashIcon from '@disy/cadenza-icons/trash.svg';
import { assertNonNullable } from '@disy/lib/custom-error';
import { icon } from '@disy/lib/icon';
import { createLabelWithIcon } from '@disy/ui/label-with-icon';
import { setupMenuButton } from '@disy/ui/menu';

import { confirm, errorAlert } from 'cadenza/alert';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import { navigateWithoutConfirmation } from 'cadenza/utils/confirm-navigation-util';
import { type WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import { getActiveOrVisibleWorksheets } from 'cadenza/workbook/state/workbook-header';
import { getViewIcon } from 'cadenza/workbook/view/view-util';
import { createAddNewViewButtonContent } from 'cadenza/workbook/workbook-add-new-view/workbook-add-new-view';
import {
  getActiveWorksheet,
  getWorkbookNameAndHash,
  getWorkbookRepositoryName,
  hasForceMaximizedView,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import type { DesignerSettingsPanelContent } from 'cadenza/workbook/workbook-panel/settings/designer-settings-panel-content';
import { areViewsOrderedAutomatically } from 'cadenza/workbook/workbook-panel/settings/worksheet/linear-layout-order-utils';
import { getSortedViews } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { activateView } from 'cadenza/workbook/workbook.state';
import {
  addWorksheetClickHandler,
  deleteWorksheet,
  getWorksheetCadenzaUrl,
} from 'cadenza/workbook/worksheet/worksheet-util';

import './worksheet-toc-panel.css';
import i18n from './worksheet-toc-panel.properties';

const COMPONENT_NAME = 'd-worksheet-toc-panel';

let addViewButtonCleaningFunction: (() => void) | undefined;

export class WorksheetTocPanel extends HTMLElement implements DesignerSettingsPanelContent {
  #updatePanelContentCallback?: () => void;
  connectedCallback() {
    this.classList.add(COMPONENT_NAME, 'd-stack-v', 'space-4');
    // observeStore because getActiveWorksheet may return undefined by the time the connectedCallback is invoked
    // e.g. when a worksheet is opened
    if (!getActiveWorksheet(getStore().getState())) {
      observeStore(getStore(), getActiveWorksheet, () => {
        if (getActiveWorksheet(getStore().getState())) {
          this.#render();
          return 'unsubscribe';
        }
      });
    } else {
      this.#render();
    }
  }

  #render() {
    const hasWorkbookManagement = isWorkbookFeatureAvailable(getStore().getState(), 'workbook-management');
    const worksheet = getActiveWorksheet(getStore().getState());
    const autoEnabled = areViewsOrderedAutomatically(worksheet.id);
    const views = getSortedViews(autoEnabled, worksheet.id);
    const addViewButtonRef = createRef();

    render(
      html`<h3 class="${COMPONENT_NAME}--title">
          <d-resource-label type="worksheet">${worksheet.printName}</d-resource-label>
        </h3>
        <div class="d-stack-h space-0_5">
          <button
            class="button button-icon"
            @click=${() => this.#showWorksheetSettings()}
            ?hidden=${!hasWorkbookManagement}
            title=${i18n('settings')}
          >
            ${icon(settingsIcon)}
          </button>
          <button
            class="button button-icon"
            @click=${() =>
              addWorksheetClickHandler(getActiveOrVisibleWorksheets(getStore().getState()), { duplicate: true })}
            ?hidden=${!hasWorkbookManagement}
            title=${i18n('duplicate')}
          >
            ${icon(copyWorksheetIcon)}
          </button>
          <button
            class="button button-icon"
            @click=${() => onDeleteClick(worksheet)}
            ?disabled=${getActiveOrVisibleWorksheets(getStore().getState()).length === 1}
            ?hidden=${!hasWorkbookManagement}
            title=${getActiveOrVisibleWorksheets(getStore().getState()).length > 1 ?
              i18n('delete.label')
            : i18n('delete.notPossible.label')}
          >
            ${icon(trashIcon)}
          </button>
        </div>
        <h3 class="${COMPONENT_NAME}--title">${i18n('views')}</h3>
        <ol class="${COMPONENT_NAME}--view-list">
          ${views.map(
            (view) =>
              html`<li
                class="${COMPONENT_NAME}--view-item keyboard-click focus-outline"
                role="link"
                tabindex="0"
                @click=${() => getStore().dispatch(activateView(view.id))}
              >
                ${createLabelWithIcon(getViewIcon(view), view.name)}
              </li>`,
          )}
        </ol>
        <button
          class="button button-icon"
          ?disabled=${hasForceMaximizedView(getStore().getState())}
          ?hidden=${!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-new-view')}
          ${ref(addViewButtonRef)}
          title=${i18n('addView')}
        >
          ${icon(addViewIcon)}
        </button>`,
      this,
    );

    this.#setupAddViewMenuButton(addViewButtonRef);
  }

  #showWorksheetSettings() {
    assertNonNullable(this.#updatePanelContentCallback);
    this.#updatePanelContentCallback();
  }

  #setupAddViewMenuButton(addViewButtonRef: Ref) {
    const addViewButtonIsEnabled =
      addViewButtonRef.value &&
      !addViewButtonRef.value.getAttribute('hidden') &&
      !addViewButtonRef.value.getAttribute('disabled');

    if (addViewButtonIsEnabled) {
      if (addViewButtonCleaningFunction) {
        addViewButtonCleaningFunction();
      }
      addViewButtonCleaningFunction = setupMenuButton(
        addViewButtonRef.value,
        async () => await createAddNewViewButtonContent(),
      );
    }
  }

  // eslint-disable-next-line accessor-pairs
  set updatePanelContentCallback(value: () => void) {
    this.#updatePanelContentCallback = value;
  }

  get settingsPanelTitle() {
    return i18n('panel.title');
  }
}

customElements.define(COMPONENT_NAME, WorksheetTocPanel);

async function onDeleteClick(activeWorksheet: WorksheetDto) {
  confirm(
    i18n('delete.confirm.title', { name: activeWorksheet.printName }),
    i18n('delete.confirm.message', { name: activeWorksheet.printName }),
    {
      submit: i18n('delete.confirm.submit'),
      submitCallback: async () => {
        const availableWorksheets = getActiveOrVisibleWorksheets(getStore().getState());
        const currentWorksheetIndex = availableWorksheets.findIndex(
          (availableWorksheet) => availableWorksheet.id === activeWorksheet.id,
        );
        const worksheetToBeOpenedId =
          availableWorksheets[currentWorksheetIndex > 0 ? currentWorksheetIndex - 1 : currentWorksheetIndex + 1].id;

        try {
          await deleteWorksheet(activeWorksheet.id);
        } catch {
          errorAlert(i18n('delete.error.title'), i18n('delete.error.message'));
          return;
        }

        navigateWithoutConfirmation(
          getWorksheetCadenzaUrl(
            getWorkbookRepositoryName(getStore().getState()),
            worksheetToBeOpenedId,
            getWorkbookNameAndHash(getStore().getState()),
          ),
        );
      },
      type: 'warning',
    },
  );
}
